import { Icon } from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { ErrorLevel } from '@xengage/gw-portals-edge-validation-js';
import { WizardContext } from '@xengage/gw-portals-wizard-react';
import classNames from 'classnames';
import _ from 'lodash';
import React, { useContext } from 'react';
import styles from './AgentRegistrationSideMenu.module.scss';
import messages from '../AgentRegistrationWizardTemplate.messages.js'

/**
 * @typedef {import('@xengage/gw-portals-edge-validation-js/errors').GenericUIIssue} GenericUIIssue
 */

/**
 * Renders an icon for steps with errors
 * @param {Array<GenericUIIssue>} errorsAndWarnings array
 * @returns {Object}
 */
function renderErrors(errorsAndWarnings = []) {
    const highestLevel = errorsAndWarnings
        .map(err => err.level)
        .reduce((currentHighest, currentError) => {
            if (!currentHighest) {
                return currentError;
            }
            if (currentHighest === ErrorLevel.LEVEL_WARN) {
                // currentError can only Greater or Equal than currentHighest
                return currentError;
            }
            return currentHighest;
        }, null);
    switch (highestLevel) {
        case ErrorLevel.LEVEL_ERROR:
            return (
                <Icon
                    icon="mi-remove_circle_outline"
                    className={styles.errorIssue}
                />
            );
        case ErrorLevel.LEVEL_WARN:
            return <Icon icon="mi-warning" className={styles.warningIssue} />;
        default:
            return null;
    }
}

async function jumpRenderModal(
    index,
    jumpTo,
    wizardContext
) {
    const modalMessages = {
        // DE15240
        title: messages.sideNavTitle,
        message: messages.sideNavMessage,
        status: 'warning',
        icon: 'gw-help-outline',
        messageProps: {
            ok: commonMessages.yes,
            close: commonMessages.close,
        },
    };
    const { showConfirm } = wizardContext

    const result = await showConfirm(modalMessages)

    if(result == 'confirm') {
        jumpTo(index)
    }
 
}

function renderSteps(
    translator,
    steps,
    currentStepIndex,
    stepsWithErrors,
    jumpTo,
    wizardContext
) {
    return steps.map((step, index) => {
        const liClassName = classNames(styles.step, {
            [styles.active]: index === currentStepIndex,
            [styles.notVisited]: currentStepIndex < index || currentStepIndex === steps.length-1,
        });
        return (
            <li className={liClassName} key={step.id}>
                <button
                    className={styles.navigationButton}
                    onClick={() => currentStepIndex > index &&
                        jumpRenderModal(
                            index,
                            jumpTo,
                            wizardContext
                        )
                    }
                    disabled={currentStepIndex < index || currentStepIndex === steps.length-1}
                    type="button"
                >
                    {translator(step.title)}
                    {renderErrors(stepsWithErrors[step.id])}
                </button>
            </li>
        );
    });
}

function AgentRegistrationSideMenu({ isTWIA, uwCompany }) {
    const wizardContext = useContext(WizardContext);
    const translator = useTranslator();
    const { steps, currentStepIndex, jumpTo, stepsWithErrors } =
        wizardContext;

    const getSidebarTitle = () => {
        if(_.isUndefined(uwCompany)) return translator(messages.AgentRegistration)
        if(isTWIA) {
            return translator(messages.TWIARegistration)
        } 
        return translator(messages.TFPARegistration)
    }

    return (
        <div className={styles.wizardSidebarWrapper}>
            <h3 className={styles.sideBarTitle}>{getSidebarTitle()}</h3>
            <div role="navigation" aria-label={getSidebarTitle()}>
                <ul className={styles.stepsNav}>
                    {renderSteps(
                        translator,
                        steps,
                        currentStepIndex,
                        stepsWithErrors,
                        jumpTo,
                        wizardContext
                    )}
                </ul>
            </div>
        </div>
    );
}

export default AgentRegistrationSideMenu;
